import {
  useGetApiV1PersonPersonidResumeeEnrollments,
  useGetApiV1PersonPersonidResumeeExams,
} from '@/shared/api/generated';

import type {
  PersonResume,
  ResumeCourseExam,
  ResumeEnrollment,
  ResumeExamCourse,
} from '../types/resume';
import { mapEnrollmentStatus } from '../utils/mapEnrollmentStatus';

/**
 * Hook para carregar o resumo acadêmico de uma pessoa.
 *
 * Realiza dois calls em paralelo:
 * - `/api/v1/person/:personId/resumee/enrollments` — dados gerais e matrículas
 * - `/api/v1/person/:personId/resumee/exams` — provas por curso
 *
 * @param personId - ID da pessoa
 * @returns Resumo mapeado com estado de loading/error
 *
 * @example
 * ```tsx
 * const { resume, isLoading } = usePersonResume(personId);
 * ```
 */
export function usePersonResume(personId: number) {
  const enrollmentsQuery = useGetApiV1PersonPersonidResumeeEnrollments(personId);
  const examsQuery = useGetApiV1PersonPersonidResumeeExams(personId);

  const isLoading = enrollmentsQuery.isLoading || examsQuery.isLoading;
  const isError = enrollmentsQuery.isError || examsQuery.isError;

  const enrollmentsData = enrollmentsQuery.data;
  const examsData = examsQuery.data;

  if (!enrollmentsData) {
    return { resume: null, isLoading, isError } as const;
  }

  const enrollments: ResumeEnrollment[] = (enrollmentsData.enrollments ?? []).map(e => ({
    courseRegistrationId: e.courseRegistrationId ?? 0,
    courseName: e.courseName ?? '',
    className: e.classStudentName ?? '',
    status: mapEnrollmentStatus(e.statusRegistration ?? 0),
    startDate: e.creationDate ?? '',
    endDate: e.dateApprovation ?? '',
    grade: null,
    attendance: null,
  }));

  const examCourses: ResumeExamCourse[] = (examsData?.exams ?? []).map(exam => ({
    courseName: exam.courseName ?? '',
    classStudentName: exam.classStudentName ?? '',
    averageGrade: exam.averageGrade ?? null,
    exams: (exam.courseExams ?? []).map(
      (ce): ResumeCourseExam => ({
        name: ce.name ?? '',
        disciplineName: ce.disciplineName ?? '',
        isRecovery: ce.isRecovery ?? false,
        grade: ce.grade ?? null,
        tryCount: ce.tryCount ?? 0,
        lastTryDate: ce.lastTryDate ?? null,
        mainStartDate: ce.mainStartDate ?? null,
        mainEndDate: ce.mainEndDate ?? null,
      }),
    ),
  }));

  const resume: PersonResume = {
    id: personId,
    fullName: enrollmentsData.fullName ?? '',
    companyName: null,
    companyProfile: enrollmentsData.companyProfile ?? null,
    companyUnit: enrollmentsData.companyUnit ?? null,
    enrollments,
    examCourses,
  };

  return { resume, isLoading, isError } as const;
}
